import {
	getColorByAlias,
	getColor,
	getHexCode,
} from '@/products/flexible-web-toolkit/colors'
import {
	getBrandColorNew,
	getBrandColorOld,
} from '@/products/flexible-web-toolkit/blocks/helpers'
import type {PageConfigContentItem, Tersed} from '@/services/kontent-ai/types'
import type {HeaderConfigContentItem} from '@/products/flexible-web-toolkit/components/header/models'

/**
 * Legacy color mappings for backward compatibility with old multiple choice values.
 * These mappings support content that was created before the migration to custom element color picker.
 */
const LEGACY_COLOR_MAPPINGS: Record<string, string> = {
	// FAQ block legacy colors (from old MultipleChoiceElement)
	beige: '#D5C4AF',
	black: '#000000',
	// Floating component legacy colors (from old MultipleChoiceElement)
	dark: '#1a1a1a',
	light: '#f5f5f5',
	none: 'transparent',
	// Used as the neutral/fallback border color in simple-content-block-with-icon
	// and similar components. Maps to Tailwind's gray-400 so the icon border
	// renders as a visible gray rather than falling through to border-theme-main.
	gray: '#9CA3AF',
}

/**
 * Resolves block-level color from new color picker and legacy dropdown fields.
 * This helper handles backward compatibility for components that have both:
 * - A new custom element color picker (string value)
 * - A legacy multiple choice dropdown (array with codename)
 *
 * Priority: New color picker \> Old dropdown
 *
 * @param newColorPicker - Value from new custom element color picker (string or undefined)
 * @param oldDropdown - Value from legacy multiple choice dropdown (array with codename)
 * @returns The resolved color string or null if neither is set
 *
 * @example
 * ```tsx
 * const blockColor = resolveBlockColor(
 *   block.elements.expandIconColorPicker,
 *   block.elements.expandIconColor
 * );
 * ```
 */
export function resolveBlockColor(
	newColorPicker: string | undefined | null,
	oldDropdown: {codename?: string}[] | undefined | null
): string | null {
	// Check new color picker first (custom element returns string)
	if (typeof newColorPicker === 'string' && newColorPicker) {
		return newColorPicker
	}

	// Fallback to old dropdown value (multiple choice returns array with codename)
	if (Array.isArray(oldDropdown) && oldDropdown.length > 0) {
		return oldDropdown[0]?.codename ?? null
	}

	return null
}

/**
 * Options for the colorWithFallback hook
 */
interface ColorWithFallbackOptions {
	/** Block-level color from custom element (highest priority) */
	blockColor?: string | null
	/** Legacy block-level color from old dropdown (array with codename) */
	blockColorLegacy?: {codename?: string}[] | null
	/** Page config to extract page-level color from */
	pageConfig?:
		| Tersed<PageConfigContentItem | HeaderConfigContentItem>
		| undefined
	/** Fallback color ID if no color is found (lowest priority) */
	fallbackColorId?: string
	/** Fallback hex color if no color is found (used when hex is needed) */
	fallbackHexColor?: string
}

/**
 * Result from the colorWithFallback hook
 */
interface ColorWithFallbackResult {
	/** The resolved color ID (from registry or CMS codename) */
	colorId: string | null
	/** The hex code for the color (if available) */
	hexCode: string | null
	/** CSS class for background color (e.g., 'bg-seresto') */
	bgClass: string | null
}

/**
 * Hook to resolve color with 5-tier fallback priority:
 *
 * Priority order (highest to lowest):
 * 1) `blockColor` from the new custom element (string value)
 * 2) `blockColorLegacy` from the legacy dropdown (first codename)
 * 3) Page-level color from new config (`getBrandColorNew`)
 * 4) Page-level color from legacy config (`getBrandColorOld`)
 * 5) `fallbackColorId` when no other source is set
 *
 * If a color is resolved, it is validated against the registry. When missing,
 * the raw CMS codename is used to preserve backward compatibility.
 *
 * @param options - Configuration options for color resolution
 * @returns Resolved color information including ID, hex code, and CSS class
 *
 * @example
 * ```tsx
 * const { colorId, hexCode, bgClass } = colorWithFallback({
 *   blockColor: block.elements.loaderColor,
 *   pageConfig: page.elements.config[0],
 *   fallbackColorId: 'elanco-blue',
 *   fallbackHexColor: '#02253e'
 * });
 * ```
 */
export function colorWithFallback(
	options: ColorWithFallbackOptions
): ColorWithFallbackResult {
	const {
		blockColor,
		blockColorLegacy,
		pageConfig,
		fallbackColorId,
		fallbackHexColor,
	} = options

	// 1-2. Block-level color (new custom element, then legacy dropdown)
	const blockLevelColor = resolveBlockColor(blockColor, blockColorLegacy)

	// 3-4. Page-level color (new then legacy)
	const pageLevelColorNew = pageConfig ? getBrandColorNew(pageConfig) : null
	const pageLevelColorOld = pageConfig ? getBrandColorOld(pageConfig) : null

	// 5. Determine selected color with priority (block new > block old > page new > page old > fallback)
	const selectedColor =
		blockLevelColor ||
		pageLevelColorNew ||
		pageLevelColorOld ||
		fallbackColorId

	// Validate color exists in registry
	const registryColor = selectedColor
		? getColorByAlias(selectedColor) || getColor(selectedColor)
		: undefined

	// Use the registry color ID if found, fallback to CMS codename for backwards compatibility
	const colorId = registryColor?.id ?? selectedColor ?? null

	// Get hex code for the color with legacy fallback support
	// First check color registry, then legacy mappings, then fallback
	let hexCode: string | null = null
	if (colorId) {
		const registryHex = getHexCode(colorId)
		if (registryHex) {
			hexCode = registryHex
		} else if (colorId in LEGACY_COLOR_MAPPINGS) {
			// Support legacy color codenames from old multiple choice elements
			hexCode = LEGACY_COLOR_MAPPINGS[colorId] ?? null
		} else {
			hexCode = fallbackHexColor ?? null
		}
	} else {
		hexCode = fallbackHexColor ?? null
	}

	// Generate background class — only for registry colours.
	// Legacy-only colours (e.g. 'gray', 'beige', 'dark') live solely in
	// LEGACY_COLOR_MAPPINGS and do not have a corresponding Tailwind safelist
	// entry, so producing `bg-gray` or `bg-dark` would silently apply a
	// broken class.  Callers that need a visual colour for legacy values
	// should use `hexCode` with an inline style instead.
	const isLegacyOnlyColor =
		colorId !== null && !registryColor && colorId in LEGACY_COLOR_MAPPINGS
	const bgClass = colorId && !isLegacyOnlyColor ? `bg-${colorId}` : null

	return {
		colorId,
		hexCode,
		bgClass,
	}
}
